import { ANALYTICS_EVENTS } from '@src/apps/constants/constant';
import {
  getUserDataForPageLoad,
  getProviderDataForPageLoad,
  parseUrlForSiteSections,
  getCurrentFlow,
  getComponentNameFromUrl
} from './storage.service.helpers';
import { matchLobWithFilterLabel } from './common.service';
// At the top of the file, add:

// Then, replace all hardcoded event names/keys in the class with references to ANALYTICS_EVENTS, e.g.:
// event: ANALYTICS_EVENTS.GLOBAL_FILTER_JOB_START,
// jobName: ANALYTICS_EVENTS.APPLY_FILTERS,
// interactionContext: ANALYTICS_EVENTS.GLOBAL_FILTERS_CONTEXT,

class StorageService {
  appEventDataLayer = 'appEventDataLayer';
  public setAdobeUserEntry(userEntry: string) {
    sessionStorage.setItem('adobeUserEntry', userEntry);
  }

  public addToEventDataLayer(windowObj) {
    if (!window[this.appEventDataLayer]) {
      window[this.appEventDataLayer] = [];
    }
    window[this.appEventDataLayer].push(windowObj);
  }
  public setAppEventDataLayerForAdobeOnInteraction(data) {
    const windowObj = {};
    windowObj['interactionName'] = data?.interactionName || this.getInteractionName();
    windowObj['interactionValue'] = data?.interactionValue;
    windowObj['event'] = ANALYTICS_EVENTS.INTERACTION;
    windowObj['interactionContext'] = data?.interactionContext;
    this.addToEventDataLayer(windowObj);
  }
  public setAppEventDataLayerForModalInteraction(event, modalName) {
    const windowObj = {};
    windowObj['event'] = event;
    windowObj['modalName'] = modalName;
    this.addToEventDataLayer(windowObj);
  }
  public setAppEventDataLayerForExitLink(exitLink, exitURL) {
    const windowObj = {};
    windowObj['event'] = ANALYTICS_EVENTS.EXIT_CLICK;
    windowObj['exitLink'] = exitLink;
    windowObj['exitURL'] = exitURL;
    this.addToEventDataLayer(windowObj);
  }
  public setAppEventDataLayerForDownloadLink(fileName) {
    this.setAppEventDataLayerForDownloadData(fileName);
  }

  public setAppEventDataLayerForAdobeOnViewInsights(taxIdValue, tinNameValue, taxIdTypeValue) {
    const windowObj = {};
    windowObj['event'] = ANALYTICS_EVENTS.JOB_COMPLETED;
    windowObj['jobName'] = ANALYTICS_EVENTS.VIEW_TAX_ID_DETAILS;
    windowObj['providerTaxId'] = taxIdValue;
    windowObj['providerTaxIdName'] = tinNameValue;
    windowObj['providerTaxIdType'] = taxIdTypeValue;
    window[this.appEventDataLayer].push(windowObj);
  }
  public setAppEventDataLayerForAdobeOnModalOpen(modalName) {
    const windowObj = {};
    windowObj['event'] = ANALYTICS_EVENTS.MODAL_VIEW;
    windowObj['modalName'] = modalName;
    window[this.appEventDataLayer] = window[this.appEventDataLayer] || [];
    this.addToEventDataLayer(windowObj);
  }
  public setAppEventDataLayerForAdobeOnModalClose(modalName) {
    const windowObj = {};
    windowObj['event'] = ANALYTICS_EVENTS.MODAL_CLOSE;
    windowObj['modalName'] = modalName;
    window[this.appEventDataLayer] = window[this.appEventDataLayer] || [];
    this.addToEventDataLayer(windowObj);
  }
  public setAppEventDataLayerForAdobeOnModalApply(modalName) {
    this.setAppEventDataLayerForAdobeOnInteraction({
      interactionName: modalName,
      interactionValue: 'Apply',
      interactionContext: ANALYTICS_EVENTS.MODAL_CTA_CLICKS,
    });
  }
  public setAppEventDataLayerForAdobeOnJobStart(job, jobName) {
    const windowObj = {};
    windowObj['event'] = job;
    windowObj['jobName'] = jobName;
    this.addToEventDataLayer(windowObj);
  }
  public setAppEventDataLayerForAdobeOnLinkClick(exitLink, exitURL) {
    const windowObj = {};
    windowObj['event'] = ANALYTICS_EVENTS.EXIT_CLICK;
    windowObj['exitLink'] = exitLink;
    windowObj['exitURL'] = exitURL;
    window[this.appEventDataLayer] = window[this.appEventDataLayer] || [];
    this.addToEventDataLayer(windowObj);
  }
  public setAppEventDataLayerForAdobeOnAccessDenied(message, status) {
    const windowObj = {};
    windowObj['event'] = 'accessDenied';
    if (message) {
      windowObj['errorMessage'] = message;
    }
    if (typeof status !== 'undefined' && status !== null) {
      windowObj['statusCode'] = status;
    }
    window[this.appEventDataLayer] = window[this.appEventDataLayer] || [];
    this.addToEventDataLayer(windowObj);
  }
  public setAppEventDataLayerForAdobeOnExportData(data) {
    const fileName = typeof data === 'string' ? data : data?.fileName;
    const pageName = typeof data === 'string' ? undefined : data?.pageName;
    this.setAppEventDataLayerForExportData(fileName, pageName, data);
  }

  public setAppEventDataLayerForGuideDownload(fileName: string) {
    this.addToEventDataLayer({
      event: 'viewerDownloadGuide',
      fileName,
    });
  }

  /**
   * Track download events with page context and user/provider metadata
   * Supports both manual page naming and automatic URL parsing
   * @param fileName - Downloaded file name
   * @param pageName - Optional page name (or pass null to auto-parse from URL)
   * @param additionalData - Optional additional download metadata
   * @example
   * // Basic download tracking
   * storageService.setAppEventDataLayerForDownloadData('guide.pdf');
   *
   * // With explicit page name and metadata
   * storageService.setAppEventDataLayerForDownloadData(
   *   'prior_auth_guide.pdf',
   *   'psms:priorauthorization',
   *   { downloadType: 'pdf' }
   * );
   */
  public setAppEventDataLayerForDownloadData(
    fileName?: string,
    pageName?: string | null,
    additionalData?: Record<string, unknown>
  ) {
    const urlData = pageName ? null : parseUrlForSiteSections();
    const finalPageName = pageName || urlData?.pageName || 'uhcinsights';
    const userData = getUserDataForPageLoad();
    const providerData = getProviderDataForPageLoad();

    const windowObj: Record<string, unknown> = {
      event: "viewerDownloadGuide",
      fileName: fileName,
      pageName: finalPageName.startsWith('uhcinsights:') ? finalPageName : `uhcinsights:${finalPageName}`,
      website: 'uhcinsights',
      businessUnit: 'uhc',
      language: 'en',
      loginStatus: 'logged in',
      msid: userData.msid,
      userType: userData.userType,
      providerUuid: providerData.providerUuid,
      corporateProviderName: providerData.corporateProviderName,
      providerTaxIdNumber: providerData.providerTaxIdNumber,
      providerTaxId: providerData.providerTaxId,
    };

    if (urlData?.sections) {
      Object.assign(windowObj, urlData.sections);
    } else if (pageName) {
      const sections = pageName.split(':');
      sections.forEach((section, index) => {
        windowObj[`siteSectionL${index + 1}`] = section;
      });
    }

    if (additionalData) {
      Object.assign(windowObj, additionalData);
    }

    this.addToEventDataLayer(windowObj);
  }

  /**
   * Track export/download events with page context and user/provider metadata
   * Supports both manual page naming and automatic URL parsing
   * @param fileName - Exported file name
   * @param pageName - Optional page name (or pass null to auto-parse from URL)
   * @param additionalData - Optional additional export metadata
   * @example
   * // Basic export tracking
   * storageService.setAppEventDataLayerForExportData('claims_report.xlsx');
   *
   * // With explicit page name and metadata
   * storageService.setAppEventDataLayerForExportData(
   *   'psms_prior_auth.csv',
   *   'psms:priorauthorization',
   *   { exportType: 'csv' }
   * );
   */
  public setAppEventDataLayerForExportData(
    fileName?: string,
    pageName?: string | null,
    additionalData?: Record<string, unknown>
  ) {
    const urlData = pageName ? null : parseUrlForSiteSections();
    const finalPageName = pageName || urlData?.pageName || 'uhcinsights';
    const userData = getUserDataForPageLoad();
    const providerData = getProviderDataForPageLoad();
    const eventName = additionalData?.event || 'viewerDownloadGuide';

    const windowObj: Record<string, unknown> = {
      event: eventName,
      fileName: fileName || additionalData?.fileName || '',
      pageName: finalPageName.startsWith('uhcinsights:') ? finalPageName : `uhcinsights:${finalPageName}`,
      website: 'uhcinsights',
      businessUnit: 'uhc',
      language: 'en',
      loginStatus: 'logged in',
      msid: userData.msid,
      userType: userData.userType,
      providerUuid: providerData.providerUuid,
      corporateProviderName: providerData.corporateProviderName,
      providerTaxIdNumber: providerData.providerTaxIdNumber,
      providerTaxId: providerData.providerTaxId,
      currentFlow: getCurrentFlow(),
    };

    if (urlData?.sections) {
      Object.assign(windowObj, urlData.sections);
    } else if (pageName) {
      const sections = pageName.split(':');
      sections.forEach((section, index) => {
        windowObj[`siteSectionL${index + 1}`] = section;
      });
    }

    if (additionalData) {
      Object.assign(windowObj, additionalData);
    }

    this.addToEventDataLayer(windowObj);
  }

  /**
   * Track page load/view events with comprehensive tracking data
   * Supports both manual page naming and automatic URL parsing
   * @param pageName - The name/section of the page (e.g., "myhome", "psms:priorauthorization")
   *                   or pass null to auto-parse from current URL
   * @param additionalData - Optional additional data to track with the page load
   * @example
   * // Manual page name
   * storageService.setAppEventDataLayerForPageLoad('myhome');
   *
   * // Auto-parse from URL
   * storageService.setAppEventDataLayerForPageLoad(null);
   *
   * // With additional data
   * storageService.setAppEventDataLayerForPageLoad('psms:priorauthorization', {
   *   customField: 'value'
   * });
   */
  public setAppEventDataLayerForPageLoad(pageName?: string | null, additionalData?: Record<string, unknown>) {
    // Parse URL if pageName is not provided
    const urlData = pageName ? null : parseUrlForSiteSections();
    const canonicalBasePageName = 'uhcinsights';
    const parsedPageName = urlData?.pageName;
    const isParsedHomePage = !pageName && parsedPageName === canonicalBasePageName;
    const finalPageName = pageName || (isParsedHomePage ? canonicalBasePageName : parsedPageName) || canonicalBasePageName;

    // Get user data (includes msid, userType, providerUuid, isInternal)
    const userData = getUserDataForPageLoad();

    // Get provider and TIN data (handles internal/external, simulation, multi-TIN)
    const providerData = getProviderDataForPageLoad();

    // Get user entry from sessionStorage (adobe or journey based)
    const adobeUserEntry = sessionStorage.getItem('adobeUserEntry');
    const journeyId = sessionStorage.getItem('journeyId');
    const userEntry = adobeUserEntry || (journeyId === '1' ? 'search by single tax id' : '');

    const windowObj: Record<string, unknown> = {
      // Core page load event data
      event: 'pageLoad',
      pageName: finalPageName.startsWith('uhcinsights:') ? finalPageName : `uhcinsights:${finalPageName}`,
      website: 'uhcinsights',
      businessUnit: 'uhc',
      language: 'en',
      loginStatus: 'logged in',
      referringSite: sessionStorage.getItem('referringSite') || document.referrer || '',
      userEntry,

      // User data
      msid: userData.msid,
      userType: userData.userType,
      providerUuid: providerData.providerUuid,

      // Provider and TIN data
      corporateProviderName: providerData.corporateProviderName,
      providerTaxIdNumber: providerData.providerTaxIdNumber,
      providerTaxId: providerData.providerTaxId,
      currentFlow: getCurrentFlow(),

      // Adobe Launch metadata
      __meta: {
        index: 0,
        validationResult: {
          active: false,
          errors: [],
          valid: null,
          validated: false
        }
      }
    };

    // Add dynamic siteSections if URL was parsed
    if (urlData?.sections) {
      Object.assign(windowObj, urlData.sections);
    } else if (pageName) {
      // Manual pageName - create siteSectionL1 from it
      const sections = pageName.split(':');
      sections.forEach((section, index) => {
        windowObj[`siteSectionL${index + 1}`] = section;
      });
    }

    // Override with any additional data passed by the developer
    if (additionalData) {
      Object.assign(windowObj, additionalData);
    }

    this.addToEventDataLayer(windowObj);
  }

  /**
   * Track print button click events
   * @param componentName - Optional: The name of the component/page being printed. If not provided, will be auto-generated from URL
   * @example
   * // With explicit component name
   * storageService.setAppEventDataLayerForPrintButtonClick('PSMS - Prior Authorization');
   *
   * // Auto-generate from URL (e.g., /PSMS/PriorAuthorization -> "PSMS - Prior Authorization")
   * storageService.setAppEventDataLayerForPrintButtonClick();
   */
  public setAppEventDataLayerForPrintButtonClick(componentName?: string) {
    const finalComponentName = componentName || getComponentNameFromUrl();

    const windowObj = {};
    windowObj['event'] = ANALYTICS_EVENTS.JOB_START;
    windowObj['jobName'] = ANALYTICS_EVENTS.PRINT_JOB_NAME;
    windowObj['componentName'] = finalComponentName;
    windowObj['currentFlow'] = getCurrentFlow();
    window[this.appEventDataLayer] = window[this.appEventDataLayer] || [];
    this.addToEventDataLayer(windowObj);
  }

  /**
   * Track print page completion events
   * @param componentName - Optional: The name of the component/page that was printed. If not provided, will be auto-generated from URL
   * @example
   * // With explicit component name
   * storageService.setAppEventDataLayerForAdobeOnPrintPageClick('PSMS - Prior Authorization');
   *
   * // Auto-generate from URL (e.g., /PSMS/PriorAuthorization -> "PSMS - Prior Authorization")
   * storageService.setAppEventDataLayerForAdobeOnPrintPageClick();
   */
  public setAppEventDataLayerForAdobeOnPrintPageClick(componentName?: string) {
    const finalComponentName = componentName || getComponentNameFromUrl();

    const windowObj = {};
    windowObj['event'] = ANALYTICS_EVENTS.JOB_COMPLETED;
    windowObj['jobName'] = ANALYTICS_EVENTS.PRINT_JOB_NAME;
    windowObj['componentName'] = finalComponentName;
    windowObj['currentFlow'] = getCurrentFlow();
    window[this.appEventDataLayer] = window[this.appEventDataLayer] || [];
    this.addToEventDataLayer(windowObj);
  }

  /**
   * Generic Compare Modal Adobe tagging helper.
   * Supports modal open/close and apply.
   */
  public setAppEventDataLayerForAdobeOnCompareModal(data: {
    action: 'open' | 'close' | 'apply';
    modalName: string;
  }) {
    const action = data.action || 'apply';
    const modalName = data.modalName;

    if (action === 'open') {
      this.setAppEventDataLayerForAdobeOnModalOpen(modalName);
      return;
    }

    if (action === 'close') {
      this.setAppEventDataLayerForAdobeOnModalClose(modalName);
      return;
    }

    if (action === 'apply') {
      this.setAppEventDataLayerForAdobeOnModalApply(modalName);
    }
  }

  /**
   * Track expand/collapse row interaction events
   * @param data - Object containing interactionName (card title) and interactionValue (label clicked)
   * @example
   * storageService.setAppEventDataLayerForExpandCollapse({ interactionName: 'Exemption Status by Tax Id', interactionValue: 'Expand All Rows' });
   * storageService.setAppEventDataLayerForExpandCollapse({ interactionName: 'Prior Authorization', interactionValue: 'Collapse All' });
   */
  public setAppEventDataLayerForExpandCollapse(data: { interactionName?: string; interactionValue?: string }) {
    const windowObj = {};
    windowObj['interactionName'] = data?.interactionName || this.getInteractionName();
    windowObj['interactionValue'] = data?.interactionValue;
    windowObj['event'] = ANALYTICS_EVENTS.INTERACTION;
    windowObj['interactionContext'] = ANALYTICS_EVENTS.PAGE_CTA_LINK_CLICKS;
    window[this.appEventDataLayer] = window[this.appEventDataLayer] || [];
    this.addToEventDataLayer(windowObj);
  }

  /**
   * Helper method to get interaction value from filter data
   * @param data - Filter data object with key and value
   * @returns Formatted interaction value string
   */
  private getInteractionVal(data: any): string {
    const filterKey = String(data?.key || '').toLowerCase();
    const value = data.value;
    const displayValue = this.getFilterDisplayValue(value);

    // Handle common filter types
    if (filterKey === 'timeperiod' || filterKey === 'pocatimeperiod') {
      return 'timeperiod:' + displayValue;
    } else if (filterKey === 'tin' || filterKey === 'taxid' || filterKey === 'pocatin') {
      return 'tin:' + JSON.stringify(value); // 'tin:' + value.Tin
    } else if (filterKey === 'npi' || filterKey === 'pocanpi') {
      return 'npi:' + displayValue;
    } else if (filterKey === 'speciality') {
      return 'specialty:' + displayValue;
    } else if (filterKey === 'lob' || filterKey === 'lineofbusiness') {
      return 'lob:' + displayValue;
    } else if (filterKey === 'viewclaimsby' || filterKey === 'dateofservice') {
      return displayValue;
    } else if (filterKey === 'states' || filterKey === 'market' || filterKey === 'region') {
      return filterKey + ':' + displayValue;
    } else if (value?.title) {
      return value.title;
    } else if (typeof value === 'string') {
      return value;
    } else {
      return JSON.stringify(value);
    }
  }

  private getFilterDisplayValue(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }

    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }

    if (Array.isArray(value)) {
      return value
        .map((item: any) => this.getFilterDisplayValue(item))
        .filter(Boolean)
        .join(', ');
    }

    return String(value.title || value.Name || value.value || value.FilterText || value.label || '');
  }

  private getInteractionName(): string {
    const splittedURL = window.location.pathname.split('/');
    const pageSegment = splittedURL.length > 2 ? splittedURL[2] : splittedURL[1];

    if (!pageSegment) {
      return 'Home';
    }

    return pageSegment.replace(/([A-Z])/g, ' $1').trim();
  }

  public setAppEventDataLayerForAdobeOnTabChange(data: any) {
    const windowObj = {};
    windowObj['interactionName'] = this.getInteractionName();
    if (data?.title) {
      windowObj['interactionValue'] = data.title + ' tab';
    } else {
      windowObj['interactionValue'] = data?.value?.title + ' tab';
    }
    windowObj['event'] = ANALYTICS_EVENTS.INTERACTION;
    windowObj['interactionContext'] = ANALYTICS_EVENTS.TAB_CLICKS;
    window[this.appEventDataLayer] = window[this.appEventDataLayer] || [];
    this.addToEventDataLayer(windowObj);
  }

  /**
   * Track page-level filter interactions
   * @param data - Filter data object containing key and value
   * @example
   * storageService.setAppEventDataLayerforAdobeOnPageLevelFilter({
   *   key: 'Timeperiod',
   *   value: { title: 'Last 12 Months' }
   * });
   */
  public setAppEventDataLayerforAdobeOnPageLevelFilter(data: any) {
    const currentUrl = window.location.pathname;
    const splittedURL = currentUrl.split('/').filter(segment => segment);
    const windowObj: any = {};

    // Determine interaction name from URL - include all segments separated by " - "
    if (splittedURL.length > 0) {
      // Join all segments with " - " without adding spaces
      // e.g., /PSMS/PriorAuthorization -> "PSMS - PriorAuthorization"
      windowObj['interactionName'] = splittedURL.join(' - ');
    } else {
      windowObj['interactionName'] = 'Home';
    }

    // Determine interaction value based on page and filter type
    if (
      currentUrl.indexOf('/Performance') > -1 ||
      currentUrl.indexOf('/QualityCareOpportunities') > -1 ||
      currentUrl.indexOf('/precheck-myscript') > -1 ||
      currentUrl.indexOf('/AlternativeMedicationsandSwitches') > -1
    ) {
      const filterKey = String(data?.key || '').toLowerCase();

      switch (filterKey) {
        case 'timeperiod':
          windowObj['interactionValue'] = 'timeperiod:' + this.getFilterDisplayValue(data?.value);
          break;
        case 'tin':
        case 'taxId':
          windowObj['interactionValue'] = 'tin:' + this.getFilterDisplayValue(JSON.stringify(data?.value)); // 'tin:' + data.value.Tin
          break;
        case 'npi':
          windowObj['interactionValue'] = 'npi:' + this.getFilterDisplayValue(data?.value);
          break;
        case 'speciality':
          windowObj['interactionValue'] = 'specialty:' + this.getFilterDisplayValue(data?.value);
          break;
        case 'lob':
        case 'lineofbusiness':
          windowObj['interactionValue'] = 'lob:' + this.getFilterDisplayValue(data?.value);
          break;
        case 'viewclaimsby':
        case 'dateofservice':
          windowObj['interactionValue'] = this.getFilterDisplayValue(data?.value);
          break;
        default:
          windowObj['interactionValue'] = this.getInteractionVal(data);
          break;
      }
    } else {
      windowObj['interactionValue'] = this.getInteractionVal(data);
    }

    window[this.appEventDataLayer] = window[this.appEventDataLayer] || [];
    windowObj['event'] = ANALYTICS_EVENTS.INTERACTION;
    windowObj['interactionContext'] = ANALYTICS_EVENTS.PAGE_FILTER;
    windowObj['currentFlow'] = getCurrentFlow();

    this.addToEventDataLayer(windowObj);
  }

  /**
   * Track global filter button (open panel) click events
   */
  public setAppEventDataLayerForAdobeOnGlobalFilterButtonClick() {
    const windowObj: Record<string, unknown> = {
      event: ANALYTICS_EVENTS.GLOBAL_FILTER_JOB_START,
      jobName: ANALYTICS_EVENTS.APPLY_FILTERS,
      currentFlow: getCurrentFlow(),
    };
    window[this.appEventDataLayer] = window[this.appEventDataLayer] || [];
    this.addToEventDataLayer(windowObj);
  }

  public setAppEventDataLayerForAdobeOnGlobalFilterReset() {
    const windowObj: Record<string, unknown> = {
      event: ANALYTICS_EVENTS.INTERACTION,
      interactionName: this.getInteractionName(),
      interactionValue: ANALYTICS_EVENTS.RESET_FILTER,
      interactionContext: ANALYTICS_EVENTS.GLOBAL_FILTERS_CONTEXT,
      currentFlow: getCurrentFlow(),
    };
    window[this.appEventDataLayer] = window[this.appEventDataLayer] || [];
    this.addToEventDataLayer(windowObj);
  }

  public setAppEventDataLayerForAdobeOnGlobalFilterSelection(selectedPage: string, filterDataObj: any) {
    console.log("HERER IN STORAGE SERVICE", selectedPage, filterDataObj);

    const windowObj: Record<string, unknown> = {
      event: ANALYTICS_EVENTS.GLOBAL_FILTER_SELECTION,
      interactionName: this.getInteractionName(),
      interactionValue: 'Global Filter Selection',
      interactionContext: ANALYTICS_EVENTS.GLOBAL_FILTERS_CONTEXT,
      currentFlow: getCurrentFlow(),
    };


    const joinValues = (list: any[], pickers: Array<(item: any) => any>) => {
      if (!Array.isArray(list)) return '';
      return list
        .map((item) => {
          for (const picker of pickers) {
            const val = picker(item);
            if (val !== undefined && val !== null && val !== '') return String(val);
          }
          return '';
        })
        .filter(Boolean)
        .join(',');
    };

    const getClaimType = (data: any) => data?.claimsFilter || data?.claimType || '';
    const getViewClaimsBy = (data: any) => data?.viewClaimsByFilter || data?.viewClaimsBy || '';
    const getProviderTaxids = (data: any) =>
      joinValues(data?.taxId || [], [
        (item) => item?.number,
        (item) => item?.Tin,
        (item) => item?.value,
        (item) => item,
      ]);
    const getIndividualProvider = (data: any) =>
      data?.npi?.Npi || data?.npi?.value || data?.individualProvider || '';
    const getIndividualSpecialty = (data: any) =>
      data?.speciality?.Speciality || data?.specialty?.Speciality || data?.specialty || '';
    const normalizePocaChannel = (value: any) => {
      if (!value) return '';
      if (typeof value === 'string') return value;
      return value?.title || value?.value || value?.name || '';
    };

    const smarteditFunction = (data: any, obj: Record<string, unknown>) => {
      obj['timePeriod'] = data?.timePeriod?.title || data?.timePeriod || '';
      obj['providerTaxId'] = getProviderTaxids(data);
      obj['contractId'] = joinValues(data?.contractId || data?.contractid || data?.ContractIds || [], [
        (item) => item?.value,
        (item) => item?.ContractId,
        (item) => item,
      ]);
      obj['lineOfBusiness'] = data?.lineOfBusiness || data?.lob || '';
      obj['individualProvider'] = getIndividualProvider(data);
      obj['specialty'] = getIndividualSpecialty(data);
    };

    switch (selectedPage) {
      case 'gettingReimbursedSummary':
      case 'paymentsPage':
      case 'nonPaymentsPage':
      case 'reconsiderationsPage':
      case 'ediSubmission':
        windowObj['claimType'] = getClaimType(filterDataObj);
        windowObj['viewClaimsBy'] = getViewClaimsBy(filterDataObj);
        windowObj['platformType'] = joinValues(filterDataObj?.platformType || [], [
          (item) => item?.value,
          (item) => item?.name,
          (item) => item,
        ]);
        break;
      case 'appealsPage':
        windowObj['viewAppealsBy'] = filterDataObj?.appealsFilter;
        break;
      case 'priorAuthorizationsPage':
        windowObj['timePeriod'] = filterDataObj?.priorAuthFilters?.timePeriod;
        windowObj['providerTaxId'] = getProviderTaxids(filterDataObj);
        windowObj['lineOfBusiness'] = filterDataObj?.priorAuthFilters?.lineOfBusiness;
        windowObj['individualProvider'] = getIndividualProvider(filterDataObj);
        windowObj['specialty'] = getIndividualSpecialty(filterDataObj);
        windowObj['channel'] = filterDataObj?.priorAuthFilters?.channel;
        windowObj['serviceType'] = filterDataObj?.priorAuthFilters?.serviceSetting;
        windowObj['serviceCategory'] = filterDataObj?.priorAuthFilters?.serviceCategory;
        break;
      case 'smartEditsPage':
        smarteditFunction(filterDataObj, windowObj);
        break;
      case 'paCodeLookUpPage':
        windowObj['timePeriod'] = filterDataObj?.selectedPoca?.timePeriod?.title;
        windowObj['providerTaxId'] = filterDataObj?.selectedPoca?.taxId?.Tin;
        windowObj['lineOfBusiness'] = filterDataObj?.selectedPoca?.lob;
        windowObj['individualProvider'] = filterDataObj?.selectedPoca?.npi?.Npi;
        windowObj['channel'] = normalizePocaChannel(filterDataObj?.selectedPoca?.channel);
        break;
      case 'displayPage':
      case 'displaySubPage':
        windowObj['timePeriod'] = filterDataObj?.topFilterTimePeriod?.FilterText;
        windowObj['providerTaxId'] = filterDataObj?.topFilterTin?.Tin;
        windowObj['contractId'] = joinValues(filterDataObj?.contractId || filterDataObj?.ContractIds || [], [
          (item) => item?.value,
          (item) => item?.ContractId,
          (item) => item,
        ]);
        windowObj['lineOfBusiness'] = filterDataObj?.dplineOfBusiness;
        windowObj['individualProvider'] = filterDataObj?.topFilterNpi?.NpiName;
        windowObj['specialty'] = filterDataObj?.topFilterSpeciality?.Speciality;
        break;
      case 'paymentIntegrity':
        windowObj['timePeriod'] = filterDataObj?.timePeriod?.title;
        windowObj['taxId'] = joinValues(filterDataObj?.taxId || [], [
          (item) => item?.number,
          (item) => item?.Tin,
          (item) => item,
        ]);
        windowObj['lineOfBusiness'] = filterDataObj?.lineOfBusiness;
        windowObj['claimType'] = filterDataObj?.claimType;
        windowObj['serviceType'] = filterDataObj?.serviceType;
        windowObj['vendor'] = filterDataObj?.vendor;
        break;
      default:
        windowObj['timePeriod'] = (filterDataObj?.timePeriod || '').replace(/([A-Z])/g, ' $1').trim();
        windowObj['providerTaxId'] = joinValues(filterDataObj?.taxId || [], [
          (item) => item?.number,
          (item) => item?.Tin,
          (item) => item,
        ]);
        windowObj['lineOfBusiness'] = matchLobWithFilterLabel(filterDataObj?.lineOfBusiness);
        break;
    }

    window[this.appEventDataLayer] = window[this.appEventDataLayer] || [];
    this.addToEventDataLayer(windowObj);
  }
}

export { StorageService }